import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import React, { ReactNode, useEffect, useState } from 'react';
import Form, { Data, FormData, FormErrors } from '../../../../../molecules/form/Form';
import IncomeVerificationIncomeSourceTypeEnum from '../../../../../enums/screening/IncomeVerificationIncomeSourceTypeEnum';
import EmploymentContent from '../provide-documents/EmploymentContent';
import styles from './ProvideDocuments.module.css';
import Text from '../../../../../atoms/typography/Text';
import { ColorEnum, FontSizeEnum, FontWeightEnum } from '../../../../../enums';
import screeningSteps, { Action } from '../../../../../organisms/screening-navigation/screeningSteps';
import { Card } from '../../../../../organisms/card';
import ScreeningNavigation from '../../../../../organisms/screening-navigation/ScreeningNavigation';
import { Trans, useTranslation } from 'react-i18next';
import SkeletonComponent from '../../../../../../components/SkeletonComponent';
import submitIncomeDocuments, { SubmitIncomeDocumentsPayload } from '../../../../../../api/screening/income-verification/submit-income-documents/submitIncomeDocuments';
import { useRoutePath } from '../../../../../utils/useRoutePath';
import findIncomeSourceDocuments, { FindIncomeSourceDocumentsResponse } from '../../../../../../api/screening/income-verification/find-income-source-documents/findIncomeSourceDocuments';
import BusinessOwnershipContent from './BusinessOwnershipContent';
import FreelanceContent from './FreelanceContent';
import { FindIncomeSourceEmploymentResponse } from '../../../../../../api/screening/income-verification/find-income-source-documents/findIncomeSourceEmployment';
import { FindIncomeSourceBusinessOwnershipResponse } from '../../../../../../api/screening/income-verification/find-income-source-documents/findIncomeSourceBusinessOwnership';
import { FindIncomeSourceFreelanceResponse } from '../../../../../../api/screening/income-verification/find-income-source-documents/findIncomeSourceFreelance';
import Notifications from '../../../../../organisms/notifications/Notifications';
import DeleteFileConfirmationModal from '../../../../../organisms/delete-file-confirmation-modal/DeleteFileConfirmationModal';
import deleteIncomeSourceDocument from '../../../../../../api/screening/income-verification/delete-income-source-document/deleteIncomeSourceDocument';
import { ErrorWithData } from '../../../../../../utils/api';
import { NotificationTypes } from '../../../../../../components/notifications/BasicNotification';
import PageContentNotFound from '../../../../../organisms/page-content-not-found/PageContentNotFound';

const ProvideDocuments = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const routePath = useRoutePath();
    const { homeseekerId, sourceId, type } = useParams();
    const [ searchParams ] = useSearchParams();

    const authorisationId = searchParams.get('authorisationId');

    const [ storedData, setStoredData ] = useState<FindIncomeSourceDocumentsResponse|null>(null);
    const [ formData, setFormData ] = useState<Data>({});
    const [ formErrors, setFormErrors ] = useState<FormErrors>({});
    const [ isFetchingDocuments, setIsFetchingDocuments ] = useState<boolean>(true);
    const [ isSubmittingDocuments, setIsSubmittingDocuments ] = useState<boolean>(false);
    const [ isSubmittingDocumentsSuccess, setIsSubmittingDocumentsSuccess ] = useState<boolean>(false);
    const [ payload, setPayload ] = useState<SubmitIncomeDocumentsPayload>(null);
    const [ incomeSourceDocumentNameToDelete, setIncomeSourceDocumentNameToDelete ] = useState<string>(null);

    useEffect(() => {
        fetchDocuments();
    }, []);

    const fetchDocuments = async () => {
        try {
            const incomeSourceDocumentsResponse = await findIncomeSourceDocuments(type.replaceAll('-', ' ') as IncomeVerificationIncomeSourceTypeEnum, sourceId);
            setStoredData(incomeSourceDocumentsResponse);
        } catch (error) {
            Notifications.add({
                type:  NotificationTypes.FAILED,
                message: t('api.request.failed')
            });
            setStoredData(null);
        } finally {
            setIsFetchingDocuments(false);
        }
    }

    const handleDocumentDelete = (key: string, name: string) => {
        const storedFileExists = storedData?.documents?.[key]?.name === name;

        if (storedFileExists) {
            setIncomeSourceDocumentNameToDelete(key);
            DeleteFileConfirmationModal.open(key, name, sourceId);
        }
    }

    const renderDocumentsForm = (): ReactNode => {
        switch(type.replaceAll('-', ' ')) {
            case IncomeVerificationIncomeSourceTypeEnum.Employment:
                return (
                    <EmploymentContent
                        storedData={storedData as FindIncomeSourceEmploymentResponse}
                        formData={formData}
                        formErrors={formErrors}
                        onChange={setPayload}
                        onDocumentDelete={handleDocumentDelete}
                    />
                );
            case IncomeVerificationIncomeSourceTypeEnum.Freelance:
                return (
                    <FreelanceContent
                        storedData={storedData as FindIncomeSourceFreelanceResponse}
                        formData={formData}
                        formErrors={formErrors}
                        onChange={setPayload}
                        onDocumentDelete={handleDocumentDelete}
                    />
                );
            case IncomeVerificationIncomeSourceTypeEnum.BusinessOwnership:
                return (
                    <BusinessOwnershipContent
                        storedData={storedData as FindIncomeSourceBusinessOwnershipResponse}
                        formData={formData}
                        formErrors={formErrors}
                        onChange={setPayload}
                        onDocumentDelete={handleDocumentDelete}
                    />
                );
            default:
                return null;
        }
    }

    const handleFormDataChange = (data: Data, _isValid: boolean, errors: FormErrors) => {
        setFormData(data);
        setFormErrors(errors);
    }

    const handleClick = (action: Action) => {
        if (action === 'previous') {
            navigate(routePath('ScreeningIncomeVerificationProvideIncomeDetails', { homeseekerId, sourceId, type }, { authorisationId }));
        }
    }

    const handleDeleteFileSuccess = () => {
        const documentNameToDelete = incomeSourceDocumentNameToDelete;

        setIncomeSourceDocumentNameToDelete(null);

        const newDocuments = {} as FindIncomeSourceDocumentsResponse['documents'];

        for (const [key, doc] of Object.entries(storedData.documents)) {
            if (key !== documentNameToDelete) {
                newDocuments[key] = doc;
            }
        }

        setStoredData({
            ...storedData,
            documents: newDocuments as unknown
        });
    }

    const handleSubmit = async () => {
        setIsSubmittingDocuments(true);
        setIsSubmittingDocumentsSuccess(false);

        try {
            await submitIncomeDocuments(type.replaceAll('-', ' ') as IncomeVerificationIncomeSourceTypeEnum, homeseekerId, sourceId, payload)
            navigate(routePath('ScreeningIncomeVerificationOverview', { homeseekerId }, { authorisationId }));
        } catch (error) {
            if ('data' in error && error.data.length > 0) {
                const errorData: ErrorWithData['data'] = error.data;
                errorData.forEach((errorItem) => {
                    Notifications.add({
                        type:  NotificationTypes.FAILED,
                        message: (
                            <Trans
                                i18nKey={errorItem.translationKey}
                                values={errorItem.params}
                                components={{ strong: <strong /> }}
                                ns="errors"
                            />
                        ),
                    })
                })
            } else {
                Notifications.add({
                    type:  NotificationTypes.FAILED,
                    message: t('api.request.failed')
                });
            }
        } finally {
            setIsSubmittingDocuments(false);
            setIsSubmittingDocumentsSuccess(false);
        }
    }

    if (!authorisationId) {
        return <PageContentNotFound/>;
    }

    if (isFetchingDocuments) {
        return (
            <div className={styles.contentContainer}>
                <div className={styles.gridLg}>
                    <Text
                        color={ColorEnum.Primary700}
                        weight={FontWeightEnum.Bold}
                        size={FontSizeEnum.DisplayXs}
                    >{t(screeningSteps.ScreeningIncomeVerificationProvideIncomeDetails.pageTitleTranslationKey)}</Text>
                    <Card elevated>
                        <div className={styles.gridMd}>
                            <SkeletonComponent height={24}/>
                            <div className={styles.gridSm}>
                                <SkeletonComponent height={24}/>
                                <SkeletonComponent height={72}/>
                            </div>
                        </div>
                    </Card>
                    <ScreeningNavigation
                        isSubmitting={isSubmittingDocuments}
                        isSuccessful={isSubmittingDocumentsSuccess}
                        disabled
                        onClick={handleClick}
                    />
                </div>
            </div>
        )
    }

    return (
        <>
            <Form
                initialFormData={storedData as unknown as FormData}
                onChange={handleFormDataChange}
                onSubmit={handleSubmit}
            >
                <div className={styles.contentContainer}>
                    <div className={styles.gridLg}>
                        <Text
                            color={ColorEnum.Primary700}
                            weight={FontWeightEnum.Bold}
                            size={FontSizeEnum.DisplayXs}
                        >{t(screeningSteps.ScreeningIncomeVerificationProvideIncomeDetails.pageTitleTranslationKey)}</Text>
                        <Card elevated>
                            {renderDocumentsForm()}
                        </Card>
                        <ScreeningNavigation
                            isSubmitting={isSubmittingDocuments}
                            isSuccessful={isSubmittingDocumentsSuccess}
                            onClick={handleClick}
                        />
                    </div>
                </div>
            </Form>
            <DeleteFileConfirmationModal
                deleteCallback={deleteIncomeSourceDocument}
                onDeleteSuccess={handleDeleteFileSuccess}
            ></DeleteFileConfirmationModal>
        </>
    )
}

export default ProvideDocuments;
